import type { estypes } from '@elastic/elasticsearch';

import {
  type AggregatedValueBetSearchResponseBody,
  type BetBucket,
  type ComputedAttributes,
  type OriginalAggregations,
  type PositiveEvAggregations,
  type StrippedValueBetSearchResponseBody,
  type ValueBetResult,
  type ValueBetSearchResponseBody,
} from '~/apps/product-plus/search-domain/pipelines/positive-ev/types';
import {
  type Loggable,
  type SearchDomainHitWithFields,
  type SearchPipelineEnrichments,
} from '~/apps/product-plus/search-domain/types';
import { type BettingInsightsElasticSearchContext } from '~/apps/product-plus/search-domain/types/contracts';
import { replaceResponseHits } from '~/apps/product-plus/search-domain/utils';
import { formatInTimeZoneWithLocale } from '~/apps/product-plus/search-domain/utils/response-factories';

type AggregatedHit = estypes.SearchHit<ValueBetResult & ComputedAttributes> & {
  _source: ValueBetResult & ComputedAttributes;
};
type ValueBetHit = estypes.SearchHit<ValueBetResult>;
type BestOddsHit = SearchDomainHitWithFields<ValueBetResult, { betId: number[] }>;

type UserPreference = { TZ: string; locale: string };

export async function createResponseFactory(
  this: Loggable,
  config: BettingInsightsElasticSearchContext,
  searchResponse: ValueBetSearchResponseBody,
  from = 0,
  hitsPerPage = 10,
  bookmakerCounts?: SearchPipelineEnrichments['bookmakerCountsByBetId'],
): Promise<AggregatedValueBetSearchResponseBody | StrippedValueBetSearchResponseBody> {
  const aggregations = searchResponse.aggregations as OriginalAggregations;
  const { baseAggregations, betsByIdBuckets, totalForPagination } = getAggregationPageData(
    aggregations,
    from,
    hitsPerPage,
  );
  const aggregatedHits = buildAggregatedHits(betsByIdBuckets, config.userPreference, bookmakerCounts);

  return replaceResponseHits(searchResponse, {
    aggregations: baseAggregations as PositiveEvAggregations,
    hits: aggregatedHits,
    isWalled: config.isWalled,
    totalRelation: 'eq',
    totalValue: totalForPagination,
  });
}

function getBookmakersCount(
  bookmakerCounts: SearchPipelineEnrichments['bookmakerCountsByBetId'] | undefined,
  betId?: number | string,
): number | undefined {
  if (!bookmakerCounts || betId === undefined) return undefined;
  return bookmakerCounts.get(String(betId));
}

function getAggregationPageData(aggregations: OriginalAggregations, from: number, hitsPerPage: number) {
  const { bets_by_id: betsByIdAgg, stable_total: stableTotalAgg, ...baseAggregations } = aggregations;
  const allBuckets = betsByIdAgg.buckets ?? [];
  const betsByIdBuckets = allBuckets.slice(from, from + hitsPerPage);
  const hasMore = allBuckets.length > from + hitsPerPage;
  const totalForPagination = hasMore ? (stableTotalAgg?.value ?? allBuckets.length) : allBuckets.length;

  return { baseAggregations, betsByIdBuckets, totalForPagination };
}

function toBestOddsHit(bucket: BetBucket): BestOddsHit | null {
  const bestOddsHit = bucket.best_odds?.hits?.hits?.[0];
  const source = bestOddsHit?._source;
  if (!bestOddsHit || !source) return null;
  return { ...bestOddsHit, _source: source, fields: { betId: [bucket.key] } };
}

function isBestOddsHit(hit: ValueBetHit | null): hit is BestOddsHit {
  return !!hit?._source;
}

function buildAggregatedHits(
  buckets: BetBucket[],
  userPreference: UserPreference,
  bookmakerCounts?: SearchPipelineEnrichments['bookmakerCountsByBetId'],
): AggregatedHit[] {
  return buckets
    .map(toBestOddsHit)
    .filter(isBestOddsHit)
    .map(mapHitsToValueBetResults(userPreference, bookmakerCounts));
}

function mapHitsToValueBetResults(
  { TZ, locale }: UserPreference,
  bookmakerCounts?: SearchPipelineEnrichments['bookmakerCountsByBetId'],
) {
  return (hit: BestOddsHit): AggregatedHit => {
    const betId = hit.fields?.betId?.[0];
    const bookmakersCount = getBookmakersCount(bookmakerCounts, betId);

    return {
      ...hit,
      _source: {
        ...hit._source,
        bookmakersCount,
        subeventStartDate: formatInTimeZoneWithLocale(hit._source.subeventStartTime, 'yyyy-MM-dd', TZ, locale),
      },
      fields: { betId: hit.fields.betId[0] },
    };
  };
}
